import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { Save, RotateCcw, Plus, Trash2, CheckSquare, Square } from 'lucide-react';

/**
 * CompanyPermissionsTab
 * Embedded permissions matrix for a specific target company.
 * Always passes targetCompanyId explicitly so the platform admin's
 * own companyId never leaks into the queries.
 */
export default function CompanyPermissionsTab({ targetCompanyId, companyName }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({}); // { category: [{ permissionId, permissionName, description, platformOnly }] }
  const [mappings, setMappings] = useState({});        // { "roleId_permId": true }
  const [changedMappings, setChangedMappings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [creatingRole, setCreatingRole] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/roles/permissions-matrix', {
        params: { targetCompanyId }
      });
      setRoles(res.data.roles);
      setPermissions(res.data.permissions);
      setMappings(res.data.mappings);
      setChangedMappings({});
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to load permissions matrix:', err);
      alert('Failed to load permissions for this company');
    } finally {
      setLoading(false);
    }
  }, [targetCompanyId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (roleId, permissionId) => {
    const key = `${roleId}_${permissionId}`;
    setMappings(prev => {
      const next = { ...prev };
      if (next[key]) { delete next[key]; } else { next[key] = true; }
      return next;
    });
    setChangedMappings(prev => ({ ...prev, [key]: !mappings[key] }));
    setHasChanges(true);
  };

  const save = async () => {
    try {
      setSaving(true);
      const roleChanges = {};
      roles.forEach(r => { roleChanges[r.roleId] = []; });
      Object.keys(mappings).forEach(key => {
        if (!mappings[key]) return;
        const [roleId, permissionId] = key.split('_');
        if (roleChanges[roleId]) roleChanges[roleId].push(permissionId);
      });
      for (const role of roles) {
        await api.put(`/roles/${role.roleId}/permissions`, {
          permissions: roleChanges[role.roleId],
          targetCompanyId,
        });
      }
      setHasChanges(false);
      setChangedMappings({});
      await load();
    } catch (err) {
      console.error('Failed to save permissions:', err);
      alert('Failed to save: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (hasChanges && !confirm('Discard unsaved changes?')) return;
    load();
  };

  const createRole = async () => {
    if (!newRoleName.trim()) { alert('Role name is required'); return; }
    try {
      setCreatingRole(true);
      await api.post('/roles', {
        roleName: newRoleName.trim(),
        description: newRoleDesc.trim(),
        targetCompanyId,
      });
      setShowAddRole(false);
      setNewRoleName('');
      setNewRoleDesc('');
      await load();
    } catch (err) {
      alert('Failed to create role: ' + (err.response?.data?.message || err.message));
    } finally {
      setCreatingRole(false);
    }
  };

  const deleteRole = async (roleId, roleName) => {
    if (!confirm(`Delete role "${roleName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/roles/${roleId}`);
      await load();
    } catch (err) {
      alert('Failed to delete role: ' + (err.response?.data?.message || err.message));
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">Loading permissions matrix…</div>
    );
  }

  const SYSTEM_ROLES = ['Company Admin', 'Finance Admin', 'Department Manager', 'Approver', 'User', 'Auditor'];
  const sortedRoles = [...roles].sort((a, b) => {
    const ai = SYSTEM_ROLES.indexOf(a.roleName);
    const bi = SYSTEM_ROLES.indexOf(b.roleName);
    if (ai === -1 && bi === -1) return a.roleName.localeCompare(b.roleName);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  const categories = Object.keys(permissions);

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Role Permissions</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage which permissions are assigned to each role for <span className="font-medium">{companyName}</span>.
            Changes here are specific to this company only.
          </p>
        </div>
        <button
          onClick={() => setShowAddRole(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg"
        >
          <Plus className="w-4 h-4" /> Add Role
        </button>
      </div>

      {/* Unsaved changes bar */}
      {hasChanges && (
        <div className="flex items-center justify-between bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-3">
          <span className="text-sm text-yellow-800">⚠ You have unsaved changes</span>
          <div className="flex gap-2">
            <button onClick={discard} className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50">
              <RotateCcw className="w-3.5 h-3.5" /> Discard
            </button>
            <button onClick={save} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60">
              <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Matrix */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left font-medium text-gray-500 uppercase text-xs border-r border-gray-200 w-72">
                Permission
              </th>
              {sortedRoles.map(role => {
                const isCustom = !SYSTEM_ROLES.includes(role.roleName);
                return (
                  <th key={role.roleId} className="px-4 py-3 text-center font-medium text-gray-500 text-xs uppercase min-w-[110px]">
                    <div className="flex flex-col items-center gap-1">
                      <span>{role.roleName}</span>
                      {role.description && (
                        <span className="text-gray-400 font-normal normal-case text-xs">{role.description}</span>
                      )}
                      {isCustom && (
                        <button
                          onClick={() => deleteRole(role.roleId, role.roleName)}
                          title="Delete custom role"
                          className="text-red-500 hover:text-red-700 flex items-center gap-0.5 text-xs font-normal normal-case"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {categories.map(category => {
              const perms = permissions[category];
              const isPlatformCat = category === 'Platform (Super Admin Only)';
              return (
                <React.Fragment key={category}>
                  {/* Category row */}
                  <tr className={isPlatformCat ? 'bg-purple-50' : 'bg-blue-50'}>
                    <td
                      colSpan={sortedRoles.length + 1}
                      className={`px-4 py-2 font-semibold text-xs uppercase tracking-wider ${isPlatformCat ? 'text-purple-700' : 'text-blue-700'}`}
                    >
                      {isPlatformCat ? '👑 ' : ''}{category}
                    </td>
                  </tr>
                  {/* Permission rows */}
                  {perms.map(perm => (
                    <tr key={perm.permissionId} className="hover:bg-gray-50">
                      <td className="sticky left-0 bg-white px-4 py-2 border-r border-gray-100">
                        <div className="font-medium text-gray-800">{perm.permissionName}</div>
                        {perm.description && (
                          <div className="text-xs text-gray-400 mt-0.5">{perm.description}</div>
                        )}
                      </td>
                      {sortedRoles.map(role => {
                        const key = `${role.roleId}_${perm.permissionId}`;
                        const checked = !!mappings[key];
                        const changed = changedMappings[key] !== undefined;
                        return (
                          <td
                            key={role.roleId}
                            className={`text-center px-4 py-2 cursor-pointer select-none transition-colors ${changed ? 'bg-yellow-50' : ''}`}
                            onClick={() => toggle(role.roleId, perm.permissionId)}
                            title={`${role.roleName}: ${checked ? 'revoke' : 'grant'} ${perm.permissionName}`}
                          >
                            {checked ? (
                              <CheckSquare className="w-5 h-5 text-blue-600 mx-auto" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-300 mx-auto hover:text-gray-500" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Role modal */}
      {showAddRole && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Add Custom Role</h3>
            <p className="text-sm text-gray-500">
              Custom role for <span className="font-medium">{companyName}</span> only.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
              <input
                type="text"
                value={newRoleName}
                onChange={e => setNewRoleName(e.target.value)}
                placeholder="e.g. Finance Viewer"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={newRoleDesc}
                onChange={e => setNewRoleDesc(e.target.value)}
                placeholder="Optional description"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setShowAddRole(false); setNewRoleName(''); setNewRoleDesc(''); }}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createRole}
                disabled={creatingRole || !newRoleName.trim()}
                className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
              >
                {creatingRole ? 'Creating…' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
